<template>
  <v-card flat class="px-3">
    <v-col
      cols="12"
      class="body-bold-smaller py-0"
    >
      <a type="primary" size="xsmall" @click="beforeLeave">
        <v-icon size="12" class="backIcon">mdi-arrow-left</v-icon>
        {{ $t('message.actions.return') }}
      </a>
    </v-col>

    <v-col class="pt-1 py-0">
      <span class="body-bold-medium">{{
        $t('message.pages.video.showPreviewThumbs')
      }}</span>
    </v-col>

    <v-col cols="12" class="py-0">
      <span class="body-regular-smaller textType6--text pageDescription">
        {{ $t('message.pages.video.messageShowPreviewThumbs') }}
        <a target="_blank" type="secondary" :href="previewThumArticle">
          <span>
            {{ $t('message.embedModal.knowMore') }}
          </span>
          <v-icon size="12">mdi-open-in-new</v-icon>
        </a>
      </span>
    </v-col>

    <v-row no-gutters class="px-2">
      <v-col cols="12">
        <BaseAlert
          v-if="hasAlternativeProgress"
          class="mb-1 mt-6"
          type="warning"
          :title="
            $t('message.pages.videoPlayerThumbnailProgress.alternativeProgress')
          "
          :description="
            $t(
              'message.pages.videoPlayerThumbnailProgress.alternativeProgressText'
            )
          "
        >
          <base-btn
            @click="handleSelectOption('alternativeProgress')"
            size="small"
            type="secondary"
            >{{ $t('message.embedModal.domainBtn') }}</base-btn
          ></BaseAlert
        >
        <BaseAlert
          v-if="!hasAlternativeProgress && !hasProgressBar"
          class="mb-1 mt-6"
          type="warning"
          :title="$t('message.pages.videoPlayerThumbnailProgress.noBar')"
          :description="
            $t('message.pages.videoPlayerThumbnailProgress.noBarText')
          "
        >
          <base-btn
            @click="handleSelectOption('controls')"
            size="small"
            type="secondary"
            >{{ $t('message.embedModal.domainBtn') }}</base-btn
          ></BaseAlert
        >

        <v-col cols="pb-0 pt-5 pl-1">
          <span class="body-bold-base">{{
            $t('message.pages.videoPlayerThumbnailProgress.previewProgressBar')
          }}</span>
        </v-col>

        <v-row>
          <v-col cols="6" class="body-regular-small pl-4">
            <span>{{
              $t('message.pages.videoPlayerThumbnailProgress.activePreview')
            }}</span>
          </v-col>
          <v-col cols="6" class="justify-end d-flex">
            <v-tooltip
              max-width="184px"
              top
              :disabled="
                hasProgressBar && !hasAlternativeProgress && hasEditVideoPerm
              "
            >
              <template v-slot:activator="{ on, attrs }">
                <div v-bind="attrs" v-on="on">
                  <v-switch
                    v-model="showPreviewThumbs"
                    class="mt-0"
                    hide-details
                    :disabled="
                      hasAlternativeProgress ||
                      !hasProgressBar ||
                      !hasEditVideoPerm
                    "
                    @click="changePlayer"
                  />
                </div>
              </template>
              <span v-if="hasAlternativeProgress && !hasProgressBar">{{
                $t('message.pages.videoPlayerThumbnailProgress.disableDummyBar')
              }}</span>
              <span v-else>{{
                $t(
                  'message.pages.videoPlayerThumbnailProgress.activeProgressBar'
                )
              }}</span>
            </v-tooltip>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <span
      v-if="internalError"
      class="red--text caption align-text ml-2 mt-2 d-block"
    >
      <v-icon color="red" size="16" class="mt-n1"> mdi-alert-circle </v-icon>
      {{ $t('message.pages.video.errors.addAlternativeProgress') }}
    </span>

    <portal to="actions-footer">
      <div class="d-flex flex-row actions-row" v-if="hasEditVideoPerm">
        <v-spacer></v-spacer>
        <v-tooltip max-width="184px" top :disabled="!withoutChanges">
          <template v-slot:activator="{ on, attrs }">
            <div class="pl-2" v-bind="attrs" v-on="on">
              <base-btn
                class="mr-2 px-4"
                text
                type="tertiary"
                :disabled="loadingButtonSave || withoutChanges"
                @click="handleUndoChanges"
              >
                {{ $t('message.cancel') }}
              </base-btn>
            </div>
          </template>
          <span>{{ $t('message.noChanges') }}</span>
        </v-tooltip>
        <v-tooltip max-width="184px" top :disabled="!withoutChanges">
          <template v-slot:activator="{ on, attrs }">
            <div class="pl-2" v-bind="attrs" v-on="on">
              <base-btn
                type="primary"
                class="px-6"
                :disabled="loadingButtonSave || withoutChanges"
                :loading="loadingButtonSave"
                @click="handleUpdate"
              >
                {{ $t('message.pages.video.publish') }}
              </base-btn>
            </div>
          </template>
          <span>{{ $t('message.noChanges') }}</span>
        </v-tooltip>
      </div>
    </portal>

    <unsaved-changes-modal />
  </v-card>
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex'
import _ from 'lodash'
import BaseAlert from '@/components/BaseAlert.vue'

import UnsavedChangesModal from '@/components/UnsavedChangesModal.vue'
import i18n from '@/translations/lang'

export default {
  name: 'VideoPlayerPreviewThumbs',
  components: {
    UnsavedChangesModal,
    BaseAlert,
  },
  props: {
    handleBack: {
      type: Function,
      required: true,
    },
    handleSelectOption: {
      type: Function,
      required: true,
    },
    liveVideoId: {
      required: false,
    },
    hasEdit: {
      type: Array,
    },
  },
  data: () => ({
    videoId: '',
    loadingButtonSave: false,
    profileId: null,
    initialConfig: {},
    showPreviewThumbs: false,
    withoutChanges: true,
    alternativeProgress: false,
  }),
  async mounted() {
    this.videoId = this.liveVideoId ?? this.$route.params.id
    this.profileId = this.$store.state.video.video.config?.profile
    this.alternativeProgress =
      this.$store.state.video.profile?.vsl.alternativeProgress
    this.showPreviewThumbs =
      this.$store.state.video.profile?.general?.showPreviewThumbs ?? true
    this.initialConfig.showPreviewThumbs = _.cloneDeep(this.showPreviewThumbs)
    this.hasEdit.push(this.checkChanges)
    this.$root.$on('discardChanges', () => {
      this.handleUndoChanges()
      this.handleBack()
    })
  },
  watch: {
    '$store.state.video.profile': {
      handler() {
        this.withoutChanges = this.checkChanges()
      },
      deep: true,
    },
  },
  computed: {
    ...mapState({
      profile: (state) => state.video.profile,
      video: (state) => state.video.video,
      profile_id: (state) => state.video.profile_id,
      internalError: (state) => state.video.internalError,
    }),
    locale() {
      return this.$i18next.resolvedLanguage
    },
    previewThumArticle() {
      if (this.locale === 'en') {
        return 'https://help.pandavideo.com/en-us/article/progress-bar-thumbnail-4e1ol1/'
      }
      if (this.locale === 'es') {
        return 'https://help.pandavideo.com/es/article/barra-de-progreso-thumbnail-1fjhj95/'
      }
      return 'https://help.pandavideo.com/pt-br/article/barra-de-progresso-thumbnail-9dy1yl/'
    },
    hasAlternativeProgress() {
      return this.alternativeProgress
    },
    hasProgressBar() {
      return this.$store.state.video.profile.controls?.includes('progress')
    },
    hasEditVideoPerm() {
      return (
        this.$store.state.user.userPermissions?.includes('put-video') ?? false
      )
    },
  },
  methods: {
    ...mapActions('video', [
      'updateProfile',
      'createProfile',
      'setProfileToVideos',
    ]),
    ...mapMutations('video', ['UPDATE_VIDEO_PROFILE', 'UPDATE_STATUS_ERROR']),
    ...mapMutations('user', ['UPDATE_GLOBAL_SNACKBAR']),
    async handleUpdate() {
      const general = this.$store.state.video.profile?.general

      this.loadingButtonSave = true

      try {
        if (!this.video.config.profile) {
          await this.createProfile(this.videoId)
          this.video.config.profile = this.profile_id
          this.profileId = this.profile_id
          await this.setProfileToVideos([this.profileId, this.videoId])
        }

        await this.updateProfile([this.profileId, { general }])

        if (this.internalError) return

        this.handleSnackbar(i18n.t('message.pages.video.changesSaved'))
        this.initialConfig.showPreviewThumbs = _.cloneDeep(
          this.showPreviewThumbs
        )
        this.withoutChanges = true
      } catch (e) {
        console.error(e)
      } finally {
        this.loadingButtonSave = false
      }
      this.$root.$emit('updateEmbedPreviewChange')
    },
    changePlayer() {
      this.profile.general.showPreviewThumbs = this.showPreviewThumbs

      this.UPDATE_STATUS_ERROR(false)
      this.UPDATE_VIDEO_PROFILE(this.profile)
      this.$root.$emit('updateEmbedPreviewChange')
    },
    handleUndoChanges() {
      this.showPreviewThumbs = _.cloneDeep(this.initialConfig.showPreviewThumbs)
      this.changePlayer()
    },
    checkChanges() {
      return (
        this.profile.general.showPreviewThumbs ===
        this.initialConfig.showPreviewThumbs
      )
    },
    beforeLeave() {
      if (!this.checkChanges()) return this.$root.$emit('unsavedChangesModal')

      this.UPDATE_STATUS_ERROR(false)
      this.handleBack()
    },
    handleSnackbar(item) {
      const text = item || i18n.t('message.changesSaved')
      const open = true

      this.UPDATE_GLOBAL_SNACKBAR({ open, text, type: 'success' })
    },
  },
}
</script>

<style lang="scss">
.backIcon {
  color: varFigma(brand-60-base) !important;
}

.backBtn {
  cursor: pointer;
}
</style>
